import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBeratungSeiten, BeratungSeite } from "@/hooks/useBeratungSeiten";
import { KundenPanel } from "./KundenPanel";
import { supabase } from "@/integrations/supabase/client";
import { fromTable } from "@/integrations/supabase/db";
import type { DrawingContent } from "./DrawingCanvas";
import { AlertCircle, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface TokenData {
  session_id: string;
  permissions: 'readonly' | 'interactive';
  session_status: string;
}

interface SessionData {
  id: string;
  status: string;
  drawing_content: any;
  form_values: Record<string, any>;
  tile_overrides: Record<string, any>;
  aktuelle_seite_id: string | null;
  ausgewaehlte_module: string[];
}

// Parse drawing_content from DB which may include shared/collaborative flags
function parseDrawingContentWithFlags(raw: any): {
  drawingContent: DrawingContent;
  shared: boolean;
  collaborative: boolean;
} {
  if (!raw) {
    return { drawingContent: { drawing: '', text: '' }, shared: false, collaborative: false };
  }
  
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return { drawingContent: { drawing: '', text: '' }, shared: false, collaborative: false };
    }
  }
  
  return {
    drawingContent: {
      drawing: raw.drawing || '',
      text: raw.text || '',
    },
    shared: raw.shared === true,
    collaborative: raw.collaborative === true,
  };
}

export function KundenViewPublic() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  // Token validation state
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Local state synced via Supabase Realtime
  const [drawingContent, setDrawingContent] = useState<DrawingContent>({ drawing: '', text: '' });
  const [isDrawingShared, setIsDrawingShared] = useState(false);
  const [isDrawingCollaborative, setIsDrawingCollaborative] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [tileOverrides, setTileOverrides] = useState<Record<string, any[]>>({});
  const [currentSeiteId, setCurrentSeiteId] = useState<string | undefined>();
  const [hoveredTileId, setHoveredTileId] = useState<string | null>(null);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError("Kein Zugangstoken angegeben.");
      setIsLoading(false);
      return;
    }
    
    const validateToken = async () => {
      try {
        // Query the public view for token validation
        const { data: tokenRecord, error: tokenError } = await supabase
          .from('beratung_session_tokens')
          .select('session_id, permissions, session_status')
          .eq('token', token)
          .single();
        
        if (tokenError || !tokenRecord) {
          setError("Ungültiger oder abgelaufener Link.");
          setIsLoading(false);
          return;
        }
        
        // Check if session is still active
        if (tokenRecord.session_status === 'beendet') {
          setError("Diese Beratungssitzung wurde bereits beendet.");
          setIsLoading(false);
          return;
        }
        
        setTokenData(tokenRecord as TokenData);
        
        // Fetch session data using the public view
        const { data: session, error: sessionError } = await supabase
          .from('beratung_sessions')
          .select('*')
          .eq('id', tokenRecord.session_id)
          .single();
        
        if (sessionError || !session) {
          setError("Sitzungsdaten konnten nicht geladen werden.");
          setIsLoading(false);
          return;
        }
        
        setSessionData(session as unknown as SessionData);
        
        // Initialize state from session
        if (session.drawing_content) {
          const parsed = parseDrawingContentWithFlags(session.drawing_content);
          setDrawingContent(parsed.drawingContent);
          setIsDrawingShared(parsed.shared);
          setIsDrawingCollaborative(parsed.collaborative);
        }
        
        if (session.form_values && typeof session.form_values === 'object') {
          setFormValues(session.form_values as Record<string, any>);
        }
        
        if (session.tile_overrides && typeof session.tile_overrides === 'object') {
          setTileOverrides(session.tile_overrides as Record<string, any[]>);
        }
        
        if (session.aktuelle_seite_id) {
          setCurrentSeiteId(session.aktuelle_seite_id);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Token validation error:', err);
        setError("Ein Fehler ist aufgetreten.");
        setIsLoading(false);
      }
    };
    
    validateToken();
  }, [token]);

  // Fetch pages once we have session data
  const { data: allSeiten = [] } = useBeratungSeiten({
    modulIds: sessionData?.ausgewaehlte_module || [],
    enabled: !!sessionData?.ausgewaehlte_module?.length,
  });

  // Supabase Realtime for cross-device synchronization
  useEffect(() => {
    if (!sessionData?.id) return;
    
    const channel = supabase
      .channel(`public-session-${sessionData.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'crm',
          table: 'beratung_sessions',
          filter: `id=eq.${sessionData.id}`,
        },
        (payload) => {
          const newData = payload.new as any;
          
          // Check if session ended
          if (newData.status === 'beendet') {
            setError("Diese Beratungssitzung wurde beendet.");
            return;
          }
          
          // Sync tile_overrides
          if (newData.tile_overrides) {
            setTileOverrides(newData.tile_overrides);
          }
          
          // Sync form_values
          if (newData.form_values) {
            setFormValues(newData.form_values);
          }
          
          // Sync current page
          if (newData.aktuelle_seite_id) {
            setCurrentSeiteId(newData.aktuelle_seite_id);
          }
          
          // Sync drawing content
          if (newData.drawing_content) {
            const parsed = parseDrawingContentWithFlags(newData.drawing_content);
            setDrawingContent(parsed.drawingContent);
            setIsDrawingShared(parsed.shared);
            setIsDrawingCollaborative(parsed.collaborative);
          }
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionData?.id]);

  // Check if user can interact based on permissions and collaborative flag
  const canInteract = tokenData?.permissions === 'interactive' && isDrawingCollaborative;

  // Handle tile selection (only if interactive)
  const handleTileSelect = useCallback((tileId: string) => {
    if (!canInteract || !sessionData?.id) return;
    
    setFormValues(prev => {
      const currentPriorities: string[] = prev['selected_priorities'] || [];
      const updated = currentPriorities.includes(tileId)
        ? currentPriorities.filter(id => id !== tileId)
        : [...currentPriorities, tileId];
      
      const newFormValues = { ...prev, selected_priorities: updated };
      
      // Persist to DB for cross-device sync
      fromTable('beratung_sessions')
        .update({ form_values: newFormValues })
        .eq('id', sessionData.id)
        .then(() => {});
      
      return newFormValues;
    });
  }, [canInteract, sessionData?.id]);

  // Handle tile hover
  const handleTileHover = useCallback((tileId: string | null) => {
    setHoveredTileId(tileId);
  }, []);

  // Handle drawing changes (only if interactive and collaborative)
  const handleDrawingChange = useCallback((newContent: DrawingContent) => {
    if (!canInteract || !sessionData?.id) return;
    
    setDrawingContent(newContent);
    
    // Persist to DB for cross-device sync
    const drawingPayload = {
      ...newContent,
      shared: isDrawingShared,
      collaborative: isDrawingCollaborative,
    };
    fromTable('beratung_sessions')
      .update({ drawing_content: drawingPayload })
      .eq('id', sessionData.id)
      .then(() => {});
  }, [canInteract, sessionData?.id, isDrawingShared, isDrawingCollaborative]);

  // Compute current seite with tile overrides applied
  const currentSeite = useMemo(() => {
    const seiteId = currentSeiteId || sessionData?.aktuelle_seite_id;
    const baseSeite = seiteId 
      ? allSeiten.find(s => s.id === seiteId) 
      : allSeiten[0];
    
    if (!baseSeite) return null;
    
    // Apply tile overrides if they exist for this page
    const overrides = tileOverrides[baseSeite.id];
    if (overrides && overrides.length > 0) {
      return {
        ...baseSeite,
        kunden_layout: {
          ...baseSeite.kunden_layout,
          tiles: overrides,
        },
      };
    }
    
    return baseSeite;
  }, [currentSeiteId, sessionData?.aktuelle_seite_id, allSeiten, tileOverrides]);

  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen w-screen bg-muted/30 flex items-center justify-center">
        <Card className="w-[400px]">
          <CardContent className="pt-6 flex flex-col items-center gap-4">
            <Clock className="h-12 w-12 text-muted-foreground animate-pulse" />
            <p className="text-lg text-muted-foreground">Verbindung wird hergestellt...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen w-screen bg-muted/30 flex items-center justify-center">
        <Card className="w-[400px]">
          <CardContent className="pt-6 flex flex-col items-center gap-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <p className="text-lg text-center">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-muted/30 flex flex-col overflow-y-auto">
      <KundenPanel
        seite={currentSeite} 
        formValues={formValues}
        drawingContent={drawingContent}
        onDrawingChange={canInteract ? handleDrawingChange : undefined}
        isDrawingShared={isDrawingShared}
        isDrawingCollaborative={canInteract}
        drawingPlacement="inline"
        fullscreen
        showHeader={false}
        interactiveTiles={canInteract}
        onTileSelect={canInteract ? handleTileSelect : undefined}
        hoveredTileId={hoveredTileId}
        onTileHover={handleTileHover}
      />
    </div>
  );
}
